import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TextField } from "@/components/poshan/FormBits";
import MasterCrud from "@/components/poshan/MasterCrud";
import EnrollmentManager from "@/components/poshan/EnrollmentManager";
import SetupGuideCard from "@/components/poshan/SetupGuideCard";
import VendorAllCategoriesPrompt from "@/components/poshan/VendorAllCategoriesPrompt";
import SetupPreflightModal from "@/components/poshan/SetupPreflightModal";
import TemplateUploadDialog from "@/components/poshan/TemplateUploadDialog";
import { Plus, Pencil, Trash2, Check, ArrowRight, School, Users, CheckCircle2, Clock, Sparkles, RefreshCw, AlertTriangle } from "lucide-react";
import { vendorApi, cchApi, enrollmentApi } from "@/lib/setupApi";
import { profileApi } from "@/lib/profilesApi";
import { useActiveSchool } from "@/lib/activeSchool";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { computeTabStatus, TAB_GUIDES, ALL_VENDOR_CATEGORIES } from "@/lib/setupGuide";
import { useFieldConfig } from "@/lib/useFieldConfig";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SETUP_TABS_KEYS = ["schools", "vendors", "cchs", "enrollment"];

// Maps the school-category chosen at registration → which class enrolment
// boxes should be visible in Setup. Each "+"-joined category lists exactly
// the classes the school operates; standalone categories are themselves the
// only class shown. Falls back to all 4 classes if the category is unknown
// or missing (older accounts predating this rule).
const CATEGORY_CLASS_MAP = {
  "BV+Primary":                          ["balVatika", "primary"],
  "BV+Primary+Upper Primary":            ["balVatika", "primary", "upperPrimary"],
  "Upper Primary (6,7,8)":               ["upperPrimary"],
  "BV+Primary+Upper Primary+High School": ["balVatika", "primary", "upperPrimary", "highSchool"],
  "High School (IX-X)":                  ["highSchool"],
};
const ALL_CLASSES = ["balVatika", "primary", "upperPrimary", "highSchool"];
function classesForCategory(category) {
  return CATEGORY_CLASS_MAP[category] || ALL_CLASSES;
}

const VENDOR_CATEGORIES = [
  { value: "grocery", label: "Grocery" },
  { value: "vegetable", label: "Vegetable" },
  { value: "egg", label: "Egg" },
  { value: "fuel", label: "Fuel" },
  { value: "ladoo", label: "Ladoo" },
  { value: "other", label: "Other" },
];
const VENDOR_FIELDS = [
  // iter124 — entity type picker. "individual" keeps the original full
  // demographic flow. "agency" skips father/husband-name fields and asks for
  // an agency name (Odia) instead. The Hand-Receipt template then drops the
  // father line for agency rows.
  {
    key: "entityType", label: "Vendor Type", type: "select", required: true,
    requiredMsg: "ବ୍ୟବସାୟୀଙ୍କ ପ୍ରକାର (Individual / Agency) ବାଛନ୍ତୁ",
    options: [
      { value: "individual", label: "Individual (ବ୍ୟକ୍ତିଗତ)" },
      { value: "agency", label: "Agency / SHG (ସଂସ୍ଥା)" },
    ],
  },
  { key: "name", label: "Vendor Name (English)", required: true, requiredMsg: "ବ୍ୟବସାୟୀଙ୍କ ନାମ (English) ବାଧ୍ୟତାମୂଳକ" },
  { key: "nameOdia", label: "Vendor Name (ଓଡ଼ିଆ)", required: true, requiredMsg: "ବ୍ୟବସାୟୀଙ୍କ ନାମ (ଓଡ଼ିଆ) ବାଧ୍ୟତାମୂଳକ" },
  { key: "category", label: "Category", type: "select", options: VENDOR_CATEGORIES, required: true, requiredMsg: "Category ବାଛନ୍ତୁ" },
  // Agency-only field: Agency Name in Odia. Hidden when type === "individual".
  {
    key: "agencyNameOdia", label: "Agency Name (ଓଡ଼ିଆ)",
    required: true, requiredMsg: "Agency ର ନାମ (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ",
    showIf: (d) => d?.entityType === "agency",
  },
  // Individual-only fields: father/husband name. Hidden when type === "agency".
  // iter153 — Relation picker gates the honorific. Field key `relationType`
  // persists on the Vendor record. Labels on the name fields recompute from
  // this value via `labelFn`.
  {
    key: "relationType",
    label: "Relation",
    type: "relation",
    required: true,
    showIf: (d) => d?.entityType !== "agency",
  },
  {
    key: "fatherName",
    labelFn: (d) => `${d?.relationType === "husband" ? "Husband" : "Father"}'s Name (English)`,
    label: "Father's / Husband's Name (English)",
    required: true,
    requiredMsg: "ପିତା/ସ୍ୱାମୀଙ୍କ ନାମ (English) ବାଧ୍ୟତାମୂଳକ",
    showIf: (d) => d?.entityType !== "agency",
  },
  {
    key: "fatherNameOdia",
    labelFn: (d) => d?.relationType === "husband" ? "ସ୍ୱାମୀଙ୍କ ନାମ (ଓଡ଼ିଆ)" : "ପିତାଙ୍କ ନାମ (ଓଡ଼ିଆ)",
    label: "Father's / Husband's Name (ଓଡ଼ିଆ)",
    required: true,
    requiredMsg: "ପିତା/ସ୍ୱାମୀଙ୍କ ନାମ (ଓଡ଼ିଆ) ବାଧ୍ୟତାମୂଳକ",
    showIf: (d) => d?.entityType !== "agency",
  },
  { key: "village", label: "Village (English)", required: true, requiredMsg: "ଗ୍ରାମ (English) ଲେଖନ୍ତୁ" },
  { key: "villageOdia", label: "Village (ଓଡ଼ିଆ)", required: true, requiredMsg: "ଗ୍ରାମ (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "post", label: "Post (English)", required: true, requiredMsg: "Post (English) ଲେଖନ୍ତୁ" },
  { key: "postOdia", label: "Post (ଓଡ଼ିଆ)", required: true, requiredMsg: "Post (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "thana", label: "Thana / Police Station (English)", required: true, requiredMsg: "Thana (English) ଲେଖନ୍ତୁ" },
  { key: "thanaOdia", label: "Thana / Police Station (ଓଡ଼ିଆ)", required: true, requiredMsg: "Thana (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "block", label: "Block (English)", required: true, requiredMsg: "Block (English) ଲେଖନ୍ତୁ" },
  { key: "blockOdia", label: "Block (ଓଡ଼ିଆ)", required: true, requiredMsg: "Block (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "district", label: "District (English)", required: true, requiredMsg: "District (English) ଲେଖନ୍ତୁ" },
  { key: "districtOdia", label: "District (ଓଡ଼ିଆ)", required: true, requiredMsg: "District (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "pincode", label: "Pincode", required: true, requiredMsg: "Pincode ଲେଖନ୍ତୁ" },
  { key: "pincodeOdia", label: "Pincode (ଓଡ଼ିଆ)", required: true, requiredMsg: "Pincode (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "phone", label: "Phone" },
];
const VENDOR_COLUMNS = [
  { key: "name", label: "Name" }, { key: "category", label: "Category" },
  { key: "village", label: "Village" }, { key: "phone", label: "Phone" },
];

const CCH_FIELDS = [
  // iter124 — same Individual / Agency picker as vendors.
  {
    key: "entityType", label: "CCH Type", type: "select", required: true,
    requiredMsg: "CCH ର ପ୍ରକାର (Individual / Agency) ବାଛନ୍ତୁ",
    options: [
      { value: "individual", label: "Individual (ବ୍ୟକ୍ତିଗତ)" },
      { value: "agency", label: "Agency / SHG (ସଂସ୍ଥା)" },
    ],
  },
  { key: "name", label: "CCH Name (English)", required: true, requiredMsg: "CCH ନାମ (English) ବାଧ୍ୟତାମୂଳକ" },
  { key: "nameOdia", label: "CCH Name (ଓଡ଼ିଆ)", required: true, requiredMsg: "CCH ନାମ (ଓଡ଼ିଆ) ବାଧ୍ୟତାମୂଳକ" },
  // Agency-only field.
  {
    key: "agencyNameOdia", label: "Agency Name (ଓଡ଼ିଆ)",
    required: true, requiredMsg: "Agency ର ନାମ (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ",
    showIf: (d) => d?.entityType === "agency",
  },
  // Individual-only fields.
  // iter153 — Relation picker (Father / Husband) gates the honorific.
  {
    key: "relationType",
    label: "Relation",
    type: "relation",
    required: true,
    showIf: (d) => d?.entityType !== "agency",
  },
  {
    key: "fatherName",
    labelFn: (d) => `${d?.relationType === "husband" ? "Husband" : "Father"}'s Name (English)`,
    label: "Father's / Husband's Name (English)",
    required: true,
    requiredMsg: "ପିତା/ସ୍ୱାମୀଙ୍କ ନାମ (English) ବାଧ୍ୟତାମୂଳକ",
    showIf: (d) => d?.entityType !== "agency",
  },
  {
    key: "fatherNameOdia",
    labelFn: (d) => d?.relationType === "husband" ? "ସ୍ୱାମୀଙ୍କ ନାମ (ଓଡ଼ିଆ)" : "ପିତାଙ୍କ ନାମ (ଓଡ଼ିଆ)",
    label: "Father's / Husband's Name (ଓଡ଼ିଆ)",
    required: true,
    requiredMsg: "ପିତା/ସ୍ୱାମୀଙ୍କ ନାମ (ଓଡ଼ିଆ) ବାଧ୍ୟତାମୂଳକ",
    showIf: (d) => d?.entityType !== "agency",
  },
  { key: "village", label: "Village (English)", required: true, requiredMsg: "ଗ୍ରାମ (English) ଲେଖନ୍ତୁ" },
  { key: "villageOdia", label: "Village (ଓଡ଼ିଆ)", required: true, requiredMsg: "ଗ୍ରାମ (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "post", label: "Post (English)", required: true, requiredMsg: "Post (English) ଲେଖନ୍ତୁ" },
  { key: "postOdia", label: "Post (ଓଡ଼ିଆ)", required: true, requiredMsg: "Post (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "thana", label: "Thana / Police Station (English)", required: true, requiredMsg: "Thana (English) ଲେଖନ୍ତୁ" },
  { key: "thanaOdia", label: "Thana / Police Station (ଓଡ଼ିଆ)", required: true, requiredMsg: "Thana (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "block", label: "Block (English)", required: true, requiredMsg: "Block (English) ଲେଖନ୍ତୁ" },
  { key: "blockOdia", label: "Block (ଓଡ଼ିଆ)", required: true, requiredMsg: "Block (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "district", label: "District (English)", required: true, requiredMsg: "District (English) ଲେଖନ୍ତୁ" },
  { key: "districtOdia", label: "District (ଓଡ଼ିଆ)", required: true, requiredMsg: "District (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  { key: "pincode", label: "Pincode", required: true, requiredMsg: "Pincode ଲେଖନ୍ତୁ" },
  { key: "pincodeOdia", label: "Pincode (ଓଡ଼ିଆ)", required: true, requiredMsg: "Pincode (ଓଡ଼ିଆ) ଲେଖନ୍ତୁ" },
  {
    key: "gender", label: "Gender", type: "select", required: true, requiredMsg: "Gender ବାଛନ୍ତୁ",
    options: [{ value: "", label: "—" }, { value: "M", label: "Male (M)" }, { value: "F", label: "Female (F)" }],
  },
  {
    key: "category", label: "Category", type: "select", required: true, requiredMsg: "Category ବାଛନ୍ତୁ",
    options: [
      { value: "", label: "—" },
      { value: "SC", label: "SC" }, { value: "ST", label: "ST" },
      { value: "OBC", label: "OBC" }, { value: "GEN", label: "GEN" },
    ],
  },
  { key: "bankAccount", label: "Bank Account No. (optional · confidential)" },
  { key: "ifsc", label: "IFSC Code (optional · confidential)" },
  { key: "phone", label: "Phone" },
  { key: "monthlyHonorarium", label: "Monthly Honorarium / Salary (₹)", type: "number", required: true, requiredMsg: "Monthly Honorarium ଲେଖନ୍ତୁ" },
  {
    key: "defaultMode", label: "Default Payment Mode", type: "select", required: true, requiredMsg: "Payment Mode ବାଛନ୍ତୁ",
    options: [
      { value: "Bank", label: "Bank" }, { value: "Cash", label: "Cash" },
      { value: "UPI", label: "UPI" }, { value: "Other", label: "Other" },
    ],
  },
];
const CCH_COLUMNS = [
  { key: "name", label: "Name" }, { key: "village", label: "Village" },
  { key: "phone", label: "Phone" }, { key: "defaultMode", label: "Mode" },
];

const PROFILE_EMPTY = {
  schoolName: "", schoolNameOdia: "",
  udiseCode: "",
  district: "", districtOdia: "",
  block: "", blockOdia: "",
  village: "", villageOdia: "",
  post: "", postOdia: "",
  thana: "", thanaOdia: "",
  pincode: "", pincodeOdia: "",
  departmentName: "School & Mass Education Department", schemeName: "PM POSHAN",
  balVatikaEnrollment: 0, primaryEnrollment: 0, upperPrimaryEnrollment: 0, highSchoolEnrollment: 0,
};

function ProfileDialog({ open, onClose, initial, onSaved }) {
  const { user } = useAuth();
  const [draft, setDraft] = useState(initial || PROFILE_EMPTY);
  // iter133 — surfaces a "UDISE already taken" modal with a 1-tap WhatsApp
  // message to admin so the school user can resolve the clash quickly.
  const [udiseConflict, setUdiseConflict] = useState(null);
  // iter112 — read admin-configurable mandatory-field map.
  const { isRequired } = useFieldConfig();
  const req = (key, fallback) => isRequired("schoolIdentity", key, fallback);

  useEffect(() => { setDraft(initial || PROFILE_EMPTY); }, [initial, open]);

  // Class enrolment boxes to show. Prefer category recorded on the profile
  // (auth.py writes it on registration via `category`), fall back to the
  // logged-in user's schoolCategory, then to all 4 classes for legacy data.
  const visibleClasses = useMemo(() => {
    return classesForCategory(initial?.category || user?.schoolCategory);
  }, [initial?.category, user?.schoolCategory]);

  const handleSave = async () => {
    const udise = (draft.udiseCode || "").trim();
    if (!draft.schoolName?.trim() || !udise) {
      toast.error("School Name and UDISE Code are required");
      return;
    }
    // iter 94 — UDISE is now collected here on Setup (registration no longer
    // asks for it). Reject the auto-generated PENDING-* placeholder so the
    // school doesn't accidentally save with one. Require 11 numeric digits.
    if (udise.startsWith("PENDING-")) {
      toast.error("Please replace the placeholder with your real 11-digit UDISE Code");
      return;
    }
    if (!(/^\d{11}$/.test(udise))) {
      toast.error("UDISE Code must be exactly 11 digits");
      return;
    }
    try {
      const res = await profileApi.upsert({ ...draft, udiseCode: udise }, draft.id);
      toast.success(initial?.id ? "School profile updated" : "School profile added");
      onSaved(res);
      onClose();
    } catch (err) {
      // iter133 — show a dedicated modal for the UDISE-already-taken case so
      // the school can ping admin via WhatsApp without leaving the page.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail === "UDISE_ALREADY_REGISTERED") {
        setUdiseConflict({ udise, schoolName: draft.schoolName, mobile: user?.mobile || "" });
        return;
      }
      toast.error("Save failed");
      console.error(err);
    }
  };

  // WhatsApp deep-link copy. Pre-fills the school's request to admin so all
  // they have to do is hit Send.
  const buildWhatsAppUrl = (info) => {
    const wa = process.env.REACT_APP_WHATSAPP_NUMBER || "918093414633";
    const lines = [
      "Namaste Admin,",
      "",
      "I tried to set my UDISE Code on the MDM Sahayak Setup page but it shows the code is already registered with another account.",
      "",
      `• UDISE Code: ${info.udise}`,
      `• School Name: ${info.schoolName || "(not yet entered)"}`,
      `• My Mobile: ${info.mobile || "(not available)"}`,
      "",
      "Please help me link this UDISE to my account. Dhanyabad!",
    ];
    return `https://wa.me/${wa}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-2xl overflow-y-auto p-0 sm:p-0">
        <div className="sticky top-0 z-10 border-b border-border bg-background px-5 pt-5 pb-4 sm:px-6">
          <DialogHeader>
            <DialogTitle>{initial?.id ? "Edit School" : "Add School"}</DialogTitle>
            <DialogDescription>School Name and UDISE Code are required.</DialogDescription>
          </DialogHeader>
        </div>
        <div className="px-5 pb-5 pt-4 sm:px-6 sm:pb-6">
        {/* Section header — surfaces UDISE prominently because registration
            no longer collects it (iter 94). */}
        <p className="mb-2 text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-clay">School Identity</p>
        {((draft.udiseCode || "").startsWith("PENDING-")) ? (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="profile-udise-pending-warning">
            <b>UDISE Code missing.</b> Enter your real 11-digit UDISE Code below — it's required for hand receipts and UC PDFs to print correctly.
          </div>
        ) : null}
        <div className="grid gap-4 py-2 sm:grid-cols-2">
          <TextField label="School Name (English)" required={req("schoolName", true)} value={draft.schoolName} onChange={(v) => setDraft((d) => ({ ...d, schoolName: v }))} testId="profile-input-name" />
          <TextField label="School Name (ଓଡ଼ିଆ)" required={req("schoolNameOdia", true)} value={draft.schoolNameOdia} onChange={(v) => setDraft((d) => ({ ...d, schoolNameOdia: v }))} testId="profile-input-name-odia" />
          <TextField
            label="UDISE Code (11 digits)"
            required={req("udiseCode", true)}
            value={(draft.udiseCode || "").startsWith("PENDING-") ? "" : (draft.udiseCode || "")}
            onChange={(v) => setDraft((d) => ({ ...d, udiseCode: v.replace(/\D/g, "").slice(0, 11) }))}
            testId="profile-input-udise"
          />
          <TextField label="District (English)" required={req("district", true)} value={draft.district} onChange={(v) => setDraft((d) => ({ ...d, district: v }))} testId="profile-input-district" />
          <TextField label="District (ଓଡ଼ିଆ)" required={req("districtOdia", true)} value={draft.districtOdia} onChange={(v) => setDraft((d) => ({ ...d, districtOdia: v }))} testId="profile-input-district-odia" />
          <TextField label="Block (English)" required={req("block", true)} value={draft.block} onChange={(v) => setDraft((d) => ({ ...d, block: v }))} testId="profile-input-block" />
          <TextField label="Block (ଓଡ଼ିଆ)" required={req("blockOdia", true)} value={draft.blockOdia} onChange={(v) => setDraft((d) => ({ ...d, blockOdia: v }))} testId="profile-input-block-odia" />
          <TextField label="Village (English)" required={req("village", true)} value={draft.village} onChange={(v) => setDraft((d) => ({ ...d, village: v }))} testId="profile-input-village" />
          <TextField label="Village (ଓଡ଼ିଆ)" required={req("villageOdia", true)} value={draft.villageOdia} onChange={(v) => setDraft((d) => ({ ...d, villageOdia: v }))} testId="profile-input-village-odia" />
          <TextField label="Post (English)" required={req("post", true)} value={draft.post} onChange={(v) => setDraft((d) => ({ ...d, post: v }))} testId="profile-input-post" />
          <TextField label="Post (ଓଡ଼ିଆ)" required={req("postOdia", true)} value={draft.postOdia} onChange={(v) => setDraft((d) => ({ ...d, postOdia: v }))} testId="profile-input-post-odia" />
          <TextField label="Thana / Police Station (English)" required={req("thana", true)} value={draft.thana} onChange={(v) => setDraft((d) => ({ ...d, thana: v }))} testId="profile-input-thana" />
          <TextField label="Thana / Police Station (ଓଡ଼ିଆ)" required={req("thanaOdia", true)} value={draft.thanaOdia} onChange={(v) => setDraft((d) => ({ ...d, thanaOdia: v }))} testId="profile-input-thana-odia" />
          <TextField label="Pincode" required={req("pincode", true)} value={draft.pincode} onChange={(v) => setDraft((d) => ({ ...d, pincode: v }))} testId="profile-input-pincode" />
          <TextField label="Pincode (ଓଡ଼ିଆ)" required={req("pincodeOdia", false)} value={draft.pincodeOdia} onChange={(v) => setDraft((d) => ({ ...d, pincodeOdia: v }))} testId="profile-input-pincode-odia" />
          <TextField label="Department" required={req("departmentName", false)} value={draft.departmentName} onChange={(v) => setDraft((d) => ({ ...d, departmentName: v }))} />
          <TextField label="Scheme" required={req("schemeName", false)} value={draft.schemeName} onChange={(v) => setDraft((d) => ({ ...d, schemeName: v }))} />
        </div>
        <div className="mt-2 rounded-lg border border-border bg-sand-50 p-4">
          <p className="mb-3 text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-clay">Class Enrollment</p>
          <p className="mb-3 text-xs text-ink-700">
            Used by Quarterly Reports A. Meal Details (auto-filled per month).
            {initial?.category || user?.schoolCategory ? (
              <> Showing classes for category <b>{initial?.category || user?.schoolCategory}</b>.</>
            ) : null}
          </p>
          {/* Static class-name map keeps Tailwind JIT-safe in production builds. */}
          <div
            className={[
              "grid gap-4",
              visibleClasses.length === 1 ? "sm:grid-cols-1" :
              visibleClasses.length === 2 ? "sm:grid-cols-2" :
              visibleClasses.length === 3 ? "sm:grid-cols-3" :
                                             "sm:grid-cols-4",
            ].join(" ")}
            data-testid="profile-enrollment-grid"
          >
            {visibleClasses.includes("balVatika") && (
              <TextField label="Bal Vatika" type="number" value={draft.balVatikaEnrollment} onChange={(v) => setDraft((d) => ({ ...d, balVatikaEnrollment: Number(v) || 0 }))} testId="profile-input-bv-enroll" />
            )}
            {visibleClasses.includes("primary") && (
              <TextField label="Primary" type="number" value={draft.primaryEnrollment} onChange={(v) => setDraft((d) => ({ ...d, primaryEnrollment: Number(v) || 0 }))} testId="profile-input-primary-enroll" />
            )}
            {visibleClasses.includes("upperPrimary") && (
              <TextField label="Upper Primary" type="number" value={draft.upperPrimaryEnrollment} onChange={(v) => setDraft((d) => ({ ...d, upperPrimaryEnrollment: Number(v) || 0 }))} testId="profile-input-up-enroll" />
            )}
            {visibleClasses.includes("highSchool") && (
              <TextField label="High School" type="number" value={draft.highSchoolEnrollment} onChange={(v) => setDraft((d) => ({ ...d, highSchoolEnrollment: Number(v) || 0 }))} testId="profile-input-hs-enroll" />
            )}
          </div>
        </div>
        <DialogFooter className="sticky bottom-0 mt-0 border-t border-border bg-background px-5 py-3 sm:px-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} data-testid="profile-save" className="bg-clay text-white hover:bg-clay-dark">
            {initial?.id ? "Update" : "Add"}
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
    {/* iter133 — UDISE-already-registered modal with 1-tap WhatsApp to admin */}
    <Dialog open={!!udiseConflict} onOpenChange={(o) => !o && setUdiseConflict(null)}>
      <DialogContent className="max-w-md" data-testid="udise-conflict-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-5 w-5" />
            UDISE already registered
          </DialogTitle>
          <DialogDescription className="pt-2 text-ink-800">
            The UDISE Code <b className="font-mono">{udiseConflict?.udise}</b>{" "}
            is already linked to another account on MDM Sahayak. For security,
            one UDISE can belong to only one user at a time.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-ink-800">
          <b>To resolve this:</b> contact the platform admin on WhatsApp.
          Tap the green button below — a ready-to-send message with your details
          will open in WhatsApp. Just hit Send.
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => setUdiseConflict(null)} data-testid="udise-conflict-cancel">
            Cancel
          </Button>
          <a
            href={udiseConflict ? buildWhatsAppUrl(udiseConflict) : "#"}
            target="_blank"
            rel="noreferrer"
            data-testid="udise-conflict-whatsapp"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.62-6.003C.122 5.281 5.405 0 11.893 0a11.82 11.82 0 0 1 8.413 3.488 11.823 11.823 0 0 1 3.483 8.414c-.003 6.554-5.286 11.875-11.776 11.875a11.94 11.94 0 0 1-5.696-1.45L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.522 5.273l-.999 3.648 3.966-1.04z" />
            </svg>
            Send WhatsApp to admin
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function SchoolsManager() {
  const { profiles, activeId, setActiveId, refresh } = useActiveSchool();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // School users see ONLY their own school (matched by schoolId from auth);
  // they cannot add, delete, or switch — only edit. Admin sees every school
  // and keeps full control.
  const visibleProfiles = isAdmin
    ? profiles
    : profiles.filter((p) => p.id === user?.schoolId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleDelete = async (id) => {
    try {
      await profileApi.remove(id);
      toast.success("School removed");
      await refresh();
    } catch (err) {
      toast.error("Delete failed");
      console.error(err);
    }
  };

  return (
    <section
      data-testid="schools-section"
      className="rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-8"
    >
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-clay">Master Data</p>
          <h2 className="mt-2 font-heading text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">{isAdmin ? "Schools" : "Your School"}</h2>
          <p className="mt-2 text-sm text-ink-700">
            {isAdmin
              ? "Add and manage every registered school — admin view."
              : "Edit your school profile. Daily Entry / Quarterly Reports use this automatically."}
          </p>
        </div>
        {isAdmin ? (
          <Button data-testid="profile-add" onClick={() => { setEditing(null); setDialogOpen(true); }} className="gap-2 bg-clay text-white hover:bg-clay-dark">
            <Plus className="h-4 w-4" /> Add School
          </Button>
        ) : (
          <Button
            data-testid="profile-edit-school"
            onClick={() => {
              const my = visibleProfiles[0] || profiles.find((p) => p.udiseCode === user?.udiseCode);
              setEditing(my || null);
              setDialogOpen(true);
            }}
            className="gap-2 bg-clay text-white hover:bg-clay-dark"
          >
            <Pencil className="h-4 w-4" /> {visibleProfiles[0] ? "Edit School Details" : "Create School Profile"}
          </Button>
        )}
      </div>

      {/* Mobile card list (<sm) — vertical, no horizontal scroll. */}
      <div className="space-y-3 sm:hidden" data-testid="schools-mobile-list">
        {visibleProfiles.length === 0 && (
          <div className="rounded-xl border-2 border-dashed border-border bg-white/50 p-6 text-center text-sm text-ink-700">
            {isAdmin ? (
              <>No schools yet. Tap <b>Add School</b> to start.</>
            ) : (
              <>No school profile linked yet. Tap <b>Create School Profile</b> above to fill your school's details.</>
            )}
          </div>
        )}
        {visibleProfiles.map((s) => (
          <article
            key={s.id}
            data-testid={`profile-card-${s.id}`}
            className={`rounded-2xl border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)] ${
              activeId === s.id ? "border-clay" : "border-border"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="font-heading text-base font-bold leading-tight text-ink-900">{s.schoolName}</h3>
                <p className="mt-0.5 text-xs text-ink-700">UDISE: <b className="text-ink-900">{s.udiseCode}</b></p>
              </div>
              {activeId === s.id ? (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-moss/15 px-2.5 py-0.5 text-[0.65rem] font-semibold text-moss-dark">
                  <Check className="h-3 w-3" /> Active
                </span>
              ) : isAdmin ? (
                <button
                  type="button"
                  data-testid={`profile-set-active-mobile-${s.id}`}
                  onClick={() => setActiveId(s.id)}
                  className="shrink-0 rounded-full border border-border bg-white px-2.5 py-1 text-[0.65rem] font-semibold text-ink-700"
                >
                  Set active
                </button>
              ) : null}
            </div>

            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
              <div>
                <dt className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">District</dt>
                <dd className="mt-0.5 text-ink-900">{s.district || "—"}</dd>
              </div>
              <div>
                <dt className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Block</dt>
                <dd className="mt-0.5 text-ink-900">{s.block || "—"}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Village</dt>
                <dd className="mt-0.5 text-ink-900">{s.village || "—"}</dd>
              </div>
            </dl>

            <div className="mt-3 flex items-center justify-end gap-2 border-t border-border pt-3">
              <Button
                size="sm"
                variant="outline"
                data-testid={`profile-edit-mobile-${s.id}`}
                onClick={() => { setEditing(s); setDialogOpen(true); }}
                className="h-9 gap-1.5 rounded-full px-4 text-xs font-bold"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit details
              </Button>
              {isAdmin && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid={`profile-delete-mobile-${s.id}`} className="h-9 w-9 rounded-full p-0 text-destructive hover:bg-red-50 hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this school?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This removes the school profile and unlinks reports. Daily entries and historical data remain in the database but won't be reachable via this profile.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(s.id)} className="bg-destructive text-white hover:bg-destructive/90">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </article>
        ))}
      </div>

      {/* Desktop / tablet table (≥sm) — unchanged. */}
      <div className="hidden overflow-x-auto rounded-xl border border-border sm:block">
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead className="bg-sand-100 text-left text-ink-700">
            <tr>
              <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Active</th>
              <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">School</th>
              <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">UDISE</th>
              <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">District / Block</th>
              <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Village</th>
              <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em] text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleProfiles.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-ink-700">
                {isAdmin ? <>No schools yet. Click <b>Add School</b> to start.</> : "No school linked to your account yet."}
              </td></tr>
            )}
            {visibleProfiles.map((s) => (
              <tr key={s.id} data-testid={`profile-row-${s.id}`} className="border-t border-border">
                <td className="p-3">
                  {activeId === s.id ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-moss/15 px-2.5 py-0.5 text-xs font-semibold text-moss-dark">
                      <Check className="h-3 w-3" /> Active
                    </span>
                  ) : isAdmin ? (
                    <Button size="sm" variant="ghost" data-testid={`profile-set-active-${s.id}`} onClick={() => setActiveId(s.id)} className="h-7 text-xs text-ink-700">
                      Set active
                    </Button>
                  ) : (
                    <span className="text-xs text-ink-700">—</span>
                  )}
                </td>
                <td className="p-3 font-semibold text-ink-900">{s.schoolName}</td>
                <td className="p-3">{s.udiseCode}</td>
                <td className="p-3">{s.district || "—"} / {s.block || "—"}</td>
                <td className="p-3">{s.village || "—"}</td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" data-testid={`profile-edit-${s.id}`} onClick={() => { setEditing(s); setDialogOpen(true); }} className="gap-1.5 text-xs">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                    {isAdmin && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`profile-delete-${s.id}`} className="text-destructive hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete school profile?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Removes the school identity record. Vendors, CCHs and daily entries are not affected.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(s.id)}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ProfileDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={editing}
        onSaved={async (r) => { await refresh(); if (!editing) setActiveId(r.id); }}
      />
    </section>
  );
}

function StepIndicator({ steps, current }) {
  return (
    <ol className="flex flex-wrap items-center gap-2 sm:gap-4">
      {steps.map((s, i) => {
        const active = i === current;
        const done = i < current;
        return (
          <li key={s} className="flex items-center gap-2">
            <div className={`grid h-8 w-8 place-items-center rounded-full border text-xs font-semibold transition-all duration-200 ${
              done ? "border-moss bg-moss text-white" : active ? "border-clay bg-clay text-white" : "border-border bg-white text-ink-700"
            }`}>
              {done ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-sm font-medium ${active ? "text-ink-900" : "text-ink-700"}`}>{s}</span>
            {i < steps.length - 1 && <ArrowRight className="hidden h-3.5 w-3.5 text-ink-700 sm:block" />}
          </li>
        );
      })}
    </ol>
  );
}

export default function Setup() {
  const { profiles, refresh, active } = useActiveSchool();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const isOnboarding = profiles.length === 0 || params.get("onboarding") === "1";
  // iter112 — admin-configurable mandatory fields.
  const { isRequired } = useFieldConfig();
  const dynamicVendorFields = useMemo(
    () => VENDOR_FIELDS.map((f) => ({ ...f, required: isRequired("vendor", f.key, !!f.required) })),
    [isRequired],
  );
  const dynamicCchFields = useMemo(
    () => CCH_FIELDS.map((f) => ({ ...f, required: isRequired("cch", f.key, !!f.required) })),
    [isRequired],
  );

  const [tab, setTab] = useState("schools");
  // Honour `?tab=` query param so deep-links from dashboard amber chips
  // land on the pending tab directly.
  useEffect(() => {
    const t = params.get("tab");
    if (t && SETUP_TABS_KEYS.includes(t) && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const [vendors, setVendors] = useState([]);
  const [cchs, setCchs] = useState([]);
  // Year-wise enrolment list — used to drive the `enrollment` tab's
  // completion status (was previously hard-coded to false which left the
  // Setup Guide stuck at 75% even after users added a Financial Year row).
  const [enrolls, setEnrolls] = useState([]);
  // iter123 — authoritative server-side completion. /api/setup-status uses
  // the admin's dynamic field_config to decide what's mandatory, so this is
  // the source of truth — the local heuristic alone was marking schools as
  // "100% complete" when admin-required fields (mobile, headmaster name,
  // Odia translations, etc.) were still blank.
  const [serverStatus, setServerStatus] = useState(null);
  useEffect(() => {
    if (user?.role !== "school") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/setup-status`);
        if (!cancelled) setServerStatus(r.data);
      } catch (e) {
        if (!cancelled) setServerStatus(null);
      }
    })();
    return () => { cancelled = true; };
    // Re-fetch whenever the underlying data changes — saves trigger refresh()
    // which mutates profiles/vendors/cchs/enrolls; we use their length+
    // updatedAt as a coarse signal.
  }, [user?.role, profiles.length, vendors.length, cchs.length, enrolls.length, active?.id]);
  // Track each tab's previous completion state so we can fire a one-shot
  // Odia "next step" toast the moment it flips false → true.
  const [vendorPromptOpen, setVendorPromptOpen] = useState(false);
  // When the user picks YES on the all-categories prompt we flip this so
  // the next save broadcasts across every vendor category. Reset to false
  // after the form closes or save completes.
  const [bulkAllCategories, setBulkAllCategories] = useState(false);

  // Derived completion state used by SetupGuideCard and "next step" toasts.
  // The active school profile is the source of truth for declared counts.
  const activeProfile = useMemo(
    () => profiles.find((p) => p.id === active?.id) || profiles[0] || null,
    [profiles, active],
  );
  const status = useMemo(
    () => computeTabStatus({ profile: activeProfile, vendors, cchs, enrolls, serverSections: serverStatus?.sections }),
    [activeProfile, vendors, cchs, enrolls, serverStatus],
  );

  // Preflight modal — shown the first time a school user lands on Setup
  // WITHOUT a `setupCountsLockedAt` timestamp on their profile. Re-openable
  // via the "Re-do Setup" button. Once the user dismisses (Cancel / X), we
  // remember it in sessionStorage so the modal doesn't keep re-opening on
  // every render — they can use the explicit "Re-do Setup" CTA later.
  const [preflightOpen, setPreflightOpen] = useState(false);
  // iter157 — Upload Filled Template dialog toggle
  const [uploadOpen, setUploadOpen] = useState(false);
  useEffect(() => {
    if (!activeProfile) return;
    if (activeProfile.setupCountsLockedAt) return;
    const dismissKey = `mds-preflight-dismissed-${activeProfile.id}`;
    if (sessionStorage.getItem(dismissKey)) return;
    setPreflightOpen(true);
  }, [activeProfile?.id, activeProfile?.setupCountsLockedAt]);

  const onPreflightClose = () => {
    if (activeProfile?.id) {
      sessionStorage.setItem(`mds-preflight-dismissed-${activeProfile.id}`, "1");
    }
    setPreflightOpen(false);
  };

  const onPreflightLocked = async () => {
    setPreflightOpen(false);
    await refresh();
    toast.success("✅ ସୂଚନା save ହୋଇଗଲା — ଏବେ ସମସ୍ତ tab ଖୋଲିଯିବ।");
  };
  const prevStatusRef = useRef({ ...status });
  useEffect(() => {
    // Fire a toast the moment any tab flips from pending → complete.
    Object.keys(status).forEach((key) => {
      const wasDone = prevStatusRef.current[key];
      if (!wasDone && status[key]) {
        const guide = TAB_GUIDES[key];
        if (guide?.completeMsg) {
          toast.success(
            // Strip HTML tags for the toast — sonner renders plain text.
            guide.completeMsg.replace(/<\/?b>/g, ""),
            { duration: 6500 },
          );
        }
      }
    });
    prevStatusRef.current = { ...status };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.schools, status.vendors, status.cchs, status.enrollment]);

  // Onboarding step derived from data state
  const step = useMemo(() => {
    if (profiles.length === 0) return 0;
    if (vendors.length === 0) return 1;
    if (cchs.length === 0) return 2;
    return 3;
  }, [profiles.length, vendors.length, cchs.length]);

  // Always keep vendors / cchs / enrolment counts fresh so the SetupGuideCard
  // status is accurate even outside the onboarding flow.
  useEffect(() => {
    (async () => {
      try {
        const [v, c] = await Promise.all([vendorApi.list(), cchApi.list()]);
        setVendors(v); setCchs(c);
      } catch {/* no-op */}
      // Enrolment list is keyed by schoolId — only fetch when we have one.
      if (activeProfile?.id) {
        try {
          const e = await enrollmentApi.list(activeProfile.id);
          setEnrolls(Array.isArray(e) ? e : []);
        } catch { setEnrolls([]); }
      } else {
        setEnrolls([]);
      }
    })();
  }, [profiles.length, tab, activeProfile?.id]);

  // Switch tab automatically as onboarding progresses
  useEffect(() => {
    if (!isOnboarding) return;
    if (step === 0) setTab("schools");
    else if (step === 1) setTab("vendors");
    else if (step === 2) setTab("cchs");
  }, [step, isOnboarding]);

  const finishOnboarding = () => {
    setParams({}); // remove ?onboarding=1
    toast.success("Setup complete — opening Quarterly Reports");
    setTimeout(() => { window.location.href = "/"; }, 600);
  };

  // ====== Vendor "Apply to all categories" plumbing ======
  // The MasterCrud Add button calls onAddVendor → we show the prompt → on
  // YES we open the form WITHOUT the Category field; on NO we open the form
  // as usual. The save handler then knows whether to broadcast.
  const [vendorOpener, setVendorOpener] = useState(null);
  const onVendorAddInterceptor = (openForm) => {
    setVendorOpener(() => openForm);
    setVendorPromptOpen(true);
  };
  const onVendorPromptChoose = (allCats) => {
    setBulkAllCategories(allCats);
    setVendorPromptOpen(false);
    if (vendorOpener) {
      // Seed with the FIRST category just to satisfy MasterCrud's required-
      // field check when broadcasting; we'll override the category per-save.
      vendorOpener(allCats ? { category: ALL_VENDOR_CATEGORIES[0] } : {});
    }
  };
  const onVendorPromptCancel = () => {
    setVendorPromptOpen(false);
    setBulkAllCategories(false);
  };

  // Broadcast save: create one record per category with the same payload.
  const vendorSaveOverride = async (draft, isEditing) => {
    if (!bulkAllCategories || isEditing) {
      // Single save — normal path.
      const saved = await vendorApi.upsert(draft, isEditing ? draft.id : undefined);
      // Reset the flag after each save.
      setBulkAllCategories(false);
      return saved;
    }
    // Bulk save — six parallel upserts, one per category.
    const records = await Promise.all(
      ALL_VENDOR_CATEGORIES.map((cat) =>
        vendorApi.upsert({ ...draft, category: cat }, undefined),
      ),
    );
    setBulkAllCategories(false);
    toast.success(`🛒 ସମସ୍ତ ${ALL_VENDOR_CATEGORIES.length} category ପାଇଁ Vendor save ହୋଇଗଲା 🎉`);
    return records;
  };

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900">
      <Navbar />
      <div className="mx-auto max-w-7xl px-3 py-6 sm:px-6 sm:py-10 lg:px-8">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3 sm:mb-6 sm:gap-4">
          <div>
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-ink-700 sm:text-xs sm:tracking-[0.28em]">
              {isOnboarding ? "Welcome — let's get you set up" : "One-time setup"}
            </p>
            <h1 className="mt-2 font-heading text-2xl font-semibold tracking-tight text-ink-900 sm:mt-3 sm:text-4xl lg:text-5xl">
              {isOnboarding ? "Onboarding" : "Setup & Master Data"}
            </h1>
            <p className="mt-2 max-w-2xl text-xs leading-relaxed text-ink-700 sm:mt-3 sm:text-base">
              {isOnboarding
                ? "Three quick steps and you're done. Add your school, list your vendors and Cook-cum-Helpers — then your Quarterly Reports and Daily Entry pages will fill in the basics for you every time."
                : "Manage your schools, vendor records and Cook-cum-Helper (CCH) staff. They're shared across Quarterly Reports and Daily Entry."}
            </p>
          </div>
          {isOnboarding && step >= 3 && (
            <Button data-testid="finish-onboarding" onClick={finishOnboarding} className="gap-2 bg-clay text-white hover:bg-clay-dark">
              Open Quarterly Reports <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
          {/* iter155 Part C — Setup Excel template.
              Downloads a 4-sheet exceljs workbook (README, School Profile,
              Vendors, CCHs) pre-filled with the active school's current
              data. Lets admins + schools batch-edit offline, then
              upload back once a parser UI ships. */}
          <Button
            variant="outline"
            size="sm"
            data-testid="setup-template-download"
            onClick={async () => {
              try {
                const { downloadSetupTemplate } = await import("@/lib/excelTemplates");
                await downloadSetupTemplate({ profile: activeProfile, vendors, cchs });
                toast.success("Setup template downloaded");
              } catch (err) {
                console.error(err);
                toast.error("Could not build template");
              }
            }}
            className="gap-1.5"
          >
            <ArrowRight className="h-3.5 w-3.5 rotate-90" />
            Download Setup Template
          </Button>
          {/* iter157 — Upload the filled template back. */}
          <Button
            variant="outline"
            size="sm"
            data-testid="setup-template-upload-btn"
            onClick={() => setUploadOpen(true)}
            className="gap-1.5"
          >
            <ArrowRight className="h-3.5 w-3.5 -rotate-90" />
            Upload Filled Template
          </Button>
        </div>

        {isOnboarding && (
          <div className="mb-8 rounded-xl border border-border bg-white p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="onboarding-stepper">
            <StepIndicator steps={["School", "Vendors", "Cook-cum-Helpers"]} current={Math.min(step, 2)} />
          </div>
        )}

        {/* "Setup ପୁନଃ ଆବଶ୍ୟକ" banner — shown when the school has existing
            vendor / cch data but never went through the preflight (legacy
            users) OR the declared counts don't match current data. */}
        {activeProfile && !preflightOpen && (() => {
          const hasLegacyData = !activeProfile.setupCountsLockedAt && (vendors.length > 0 || cchs.length > 0);
          if (!hasLegacyData) return null;
          return (
            <section
              data-testid="setup-relock-banner"
              className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 shadow-sm sm:p-5"
            >
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-900">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  ⚠️ Setup ପୁନଃ ଆବଶ୍ୟକ — ଆପଣଙ୍କ ତଥ୍ୟ ଆଗରୁ update ହୋଇଛି
                </p>
                <p className="mt-0.5 text-xs text-amber-900">
                  ଆପଣଙ୍କ ବିଦ୍ୟାଳୟ ପାଇଁ Vendor / CCH ସଂଖ୍ୟା ଏବେ ଯାଏ declare ହୋଇନାହିଁ। ତଳର button ଦବାଇ ସଂଖ୍ୟା save କରନ୍ତୁ — ତାହାହେଲେ profile completion ସଠିକ୍ ଭାବେ ଗଣିହେବ।
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  if (activeProfile?.id) {
                    sessionStorage.removeItem(`mds-preflight-dismissed-${activeProfile.id}`);
                  }
                  setPreflightOpen(true);
                }}
                data-testid="setup-relock-cta"
                className="gap-1 bg-amber-600 text-white hover:bg-amber-700"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Re-do Setup
              </Button>
            </section>
          );
        })()}

        {/* "100% complete" celebration banner — only shown when all 4 tabs done. */}
        {activeProfile && status.schools && status.vendors && status.cchs && status.enrollment && (
          <section
            data-testid="setup-complete-banner"
            className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-moss bg-gradient-to-r from-moss/20 to-moss/5 p-4 shadow-sm sm:p-5"
          >
            <Sparkles className="h-6 w-6 shrink-0 text-moss" />
            <p className="flex-1 text-sm font-bold text-moss-dark sm:text-base">
              🎉 100% Profile Complete — ସମସ୍ତ Setup ସମ୍ପୂର୍ଣ୍ଣ! ଏବେ ଆପଣ Daily Entry ଓ Quarterly Reports ବ୍ୟବହାର କରିପାରିବେ।
            </p>
          </section>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <div className="overflow-x-auto sm:overflow-visible">
            <TabsList className="bg-sand-100 p-1 flex w-max min-w-full sm:w-auto sm:min-w-0">
              <TabsTrigger value="schools" data-testid="setup-tab-schools" className="data-[state=active]:bg-clay data-[state=active]:text-white">
                <School className="mr-1.5 h-3.5 w-3.5" />
                <span className="hidden sm:inline">Schools</span>
                <span className="sm:hidden">School</span>
                {status.schools ? (
                  <CheckCircle2 className="ml-1.5 h-3.5 w-3.5 text-moss-dark data-[state=active]:text-white" />
                ) : (
                  <Clock className="ml-1.5 h-3.5 w-3.5 text-amber-700 data-[state=active]:text-white" />
                )}
              </TabsTrigger>
              <TabsTrigger value="vendors" data-testid="setup-tab-vendors" className="data-[state=active]:bg-clay data-[state=active]:text-white">
                Vendors
                {status.vendors ? (
                  <CheckCircle2 className="ml-1.5 h-3.5 w-3.5 text-moss-dark data-[state=active]:text-white" />
                ) : (
                  <Clock className="ml-1.5 h-3.5 w-3.5 text-amber-700 data-[state=active]:text-white" />
                )}
              </TabsTrigger>
              <TabsTrigger value="cchs" data-testid="setup-tab-cchs" className="data-[state=active]:bg-clay data-[state=active]:text-white">
                <span className="hidden sm:inline">Cook-cum-Helpers</span>
                <span className="sm:hidden">CCH</span>
                {status.cchs ? (
                  <CheckCircle2 className="ml-1.5 h-3.5 w-3.5 text-moss-dark data-[state=active]:text-white" />
                ) : (
                  <Clock className="ml-1.5 h-3.5 w-3.5 text-amber-700 data-[state=active]:text-white" />
                )}
              </TabsTrigger>
              <TabsTrigger value="enrollment" data-testid="setup-tab-enrollment" className="data-[state=active]:bg-clay data-[state=active]:text-white">
                <Users className="mr-1.5 h-3.5 w-3.5" />
                <span className="hidden sm:inline">Year-wise Enrolment</span>
                <span className="sm:hidden">Enrolment</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Odia guide card sits below the tabs and re-renders per active tab. */}
          <div className="mt-5">
            <SetupGuideCard
              activeTab={tab}
              profile={activeProfile}
              vendors={vendors}
              cchs={cchs}
              enrolls={enrolls}
              onJumpTab={setTab}
              serverSections={serverStatus?.sections}
              serverMissingBySection={serverStatus?.missingBySection}
            />
          </div>

          <TabsContent value="schools" className="mt-3">
            <SchoolsManager />
          </TabsContent>

          <TabsContent value="vendors" className="mt-3">
            <MasterCrud
              title="Vendors"
              testIdPrefix="vendor"
              api={{
                list: async () => { const d = await vendorApi.list(); setVendors(d); return d; },
                upsert: vendorApi.upsert,
                remove: vendorApi.remove,
              }}
              columns={VENDOR_COLUMNS}
              formFields={dynamicVendorFields}
              emptyRecord={{ entityType: "individual", name: "", nameOdia: "", agencyNameOdia: "", relationType: "father", fatherName: "", fatherNameOdia: "", village: "", villageOdia: "", post: "", postOdia: "", thana: "", thanaOdia: "", block: "", blockOdia: "", district: "", districtOdia: "", pincode: "", pincodeOdia: "", address: "", addressOdia: "", phone: "", category: "grocery", schoolIds: [] }}
              copyFromGroupKey="category"
              addInterceptor={onVendorAddInterceptor}
              saveOverride={vendorSaveOverride}
              hideFieldKeys={bulkAllCategories ? ["category"] : []}
            />
          </TabsContent>

          <TabsContent value="cchs" className="mt-3">
            <MasterCrud
              title="Cook-cum-Helpers"
              testIdPrefix="cch"
              api={{
                list: async () => { const d = await cchApi.list(); setCchs(d); return d; },
                upsert: cchApi.upsert,
                remove: cchApi.remove,
              }}
              columns={CCH_COLUMNS}
              formFields={dynamicCchFields}
              emptyRecord={{ entityType: "individual", name: "", nameOdia: "", agencyNameOdia: "", relationType: "father", fatherName: "", fatherNameOdia: "", village: "", villageOdia: "", post: "", postOdia: "", thana: "", thanaOdia: "", block: "", blockOdia: "", district: "", districtOdia: "", pincode: "", pincodeOdia: "", address: "", addressOdia: "", gender: "", category: "", bankAccount: "", ifsc: "", phone: "", monthlyHonorarium: 0, defaultMode: "Bank", schoolIds: [] }}
              copyFromGroupKey="village"
            />
          </TabsContent>

          <TabsContent value="enrollment" className="mt-3">
            {(() => {
              // Prefer the persisted school_profile.category. Fall back to
              // user.schoolCategory (the auth record) for legacy profiles
              // that were created before `category` was persisted.
              const effectiveCategory = active?.category || user?.schoolCategory || "";
              return (
                <EnrollmentManager
                  schoolId={active?.id || ""}
                  schoolName={active?.schoolName || ""}
                  visibleClasses={classesForCategory(effectiveCategory)}
                  category={effectiveCategory}
                />
              );
            })()}
          </TabsContent>
        </Tabs>

        {/* Vendor "Apply to all categories?" pre-flight prompt */}
        <VendorAllCategoriesPrompt
          open={vendorPromptOpen}
          onChoose={onVendorPromptChoose}
          onCancel={onVendorPromptCancel}
        />

        {/* First-visit preflight — locks the declared vendor categories +
            cch count. Also reopens via the "Re-do Setup" CTA. Only render
            when we have a profile loaded — there's no point asking for
            counts before the school identity exists. */}
        {activeProfile && (
          <SetupPreflightModal
            open={preflightOpen}
            profile={activeProfile}
            onLocked={onPreflightLocked}
            onClose={onPreflightClose}
          />
        )}
        {uploadOpen && (
          <TemplateUploadDialog
            kind="setup"
            activeProfile={activeProfile}
            schoolId={activeProfile?.id || ""}
            onClose={() => setUploadOpen(false)}
            onApplied={async () => { await refresh?.(); }}
          />
        )}
      </div>
    </main>
  );
}
